import type { SnakeSegment } from '../stores/gameStore';

interface FrameData {
  snake: SnakeSegment[];
  food: { x: number; y: number } | null;
  score: number;
  currentLevel: 1 | 2 | 3;
  phase: 'menu' | 'levelSelect' | 'playing' | 'paused' | 'gameOver' | 'highScores' | 'settings';
  colorblindMode: boolean;
  gridWidth: number;
  gridHeight: number;
  shake: { x: number; y: number };
}

export const drawFrame = (ctx: CanvasRenderingContext2D, data: FrameData) => {
  const { canvas } = ctx;
  const w = canvas.width;
  const h = canvas.height;
  
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.save();
  ctx.translate(data.shake.x, data.shake.y);
  
  // Calculate cell size to fit grid in canvas
  const cellSize = Math.min(
    Math.floor(w / data.gridWidth),
    Math.floor(h / data.gridHeight)
  );
  const gridOffsetX = (w - cellSize * data.gridWidth) / 2;
  const gridOffsetY = (h - cellSize * data.gridHeight) / 2;
  
  // Draw grid background
  drawGrid(ctx, data.gridWidth, data.gridHeight, cellSize, gridOffsetX, gridOffsetY, data.colorblindMode);
  
  // Draw food
  if (data.food && data.phase === 'playing') {
    drawFood(ctx, data.food, cellSize, gridOffsetX, gridOffsetY, data.colorblindMode);
  }
  
  // Draw snake
  if (data.snake.length > 0 && (data.phase === 'playing' || data.phase === 'paused')) {
    drawSnake(ctx, data.snake, cellSize, gridOffsetX, gridOffsetY, data.colorblindMode);
  }
  
  // Draw scanline effect (retro CRT feel)
  drawScanlines(ctx, w, h);
  
  // Draw HUD
  if (data.phase === 'playing' || data.phase === 'paused') {
    drawHud(ctx, data.score, data.currentLevel, w);
  }
  
  // Draw pause overlay
  if (data.phase === 'paused') {
    drawPauseOverlay(ctx, w, h);
  }
  
  ctx.restore();
};

const drawGrid = (
  ctx: CanvasRenderingContext2D,
  gridWidth: number,
  gridHeight: number,
  cellSize: number,
  offsetX: number,
  offsetY: number,
  highContrast: boolean
) => {
  // Dark background
  ctx.fillStyle = highContrast ? '#0a0a0a' : '#001100';
  ctx.fillRect(offsetX, offsetY, cellSize * gridWidth, cellSize * gridHeight);
  
  // Grid lines (subtle)
  ctx.strokeStyle = highContrast ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 255, 0, 0.1)';
  ctx.lineWidth = 1;
  
  for (let x = 0; x <= gridWidth; x++) {
    ctx.beginPath();
    ctx.moveTo(offsetX + x * cellSize, offsetY);
    ctx.lineTo(offsetX + x * cellSize, offsetY + gridHeight * cellSize);
    ctx.stroke();
  }
  
  for (let y = 0; y <= gridHeight; y++) {
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY + y * cellSize);
    ctx.lineTo(offsetX + gridWidth * cellSize, offsetY + y * cellSize);
    ctx.stroke();
  }
};

const drawSnake = (
  ctx: CanvasRenderingContext2D,
  snake: SnakeSegment[],
  cellSize: number,
  offsetX: number,
  offsetY: number,
  highContrast: boolean
) => {
  const snakeColor = highContrast ? '#00ff00' : '#00FF00';
  const headColor = highContrast ? '#88ff88' : '#88FF88';
  
  // Draw body segments
  for (let i = 0; i < snake.length; i++) {
    const segment = snake[i];
    const x = offsetX + segment.x * cellSize;
    const y = offsetY + segment.y * cellSize;
    
    if (i === 0) {
      // Head - brighter with eyes
      ctx.fillStyle = headColor;
      ctx.fillRect(x + 1, y + 1, cellSize - 2, cellSize - 2);
      
      // Eyes
      const eyeSize = Math.max(2, cellSize / 6);
      ctx.fillStyle = '#000000';
      ctx.fillRect(x + cellSize * 0.25, y + cellSize * 0.3, eyeSize, eyeSize);
      ctx.fillRect(x + cellSize * 0.75 - eyeSize, y + cellSize * 0.3, eyeSize, eyeSize);
    } else {
      // Body segments
      ctx.fillStyle = snakeColor;
      ctx.fillRect(x + 1, y + 1, cellSize - 2, cellSize - 2);
      
      // Subtle outline
      ctx.strokeStyle = highContrast ? '#ffffff' : '#00aa00';
      ctx.lineWidth = 1;
      ctx.strokeRect(x + 1, y + 1, cellSize - 2, cellSize - 2);
    }
  }
};

const drawFood = (
  ctx: CanvasRenderingContext2D,
  food: { x: number; y: number },
  cellSize: number,
  offsetX: number,
  offsetY: number,
  highContrast: boolean
) => {
  const x = offsetX + food.x * cellSize;
  const y = offsetY + food.y * cellSize;
  
  // Pulsing effect
  const pulse = 0.8 + Math.sin(performance.now() / 200) * 0.2;
  const size = (cellSize - 2) * pulse;
  const centerX = x + cellSize / 2;
  const centerY = y + cellSize / 2;
  
  // Red apple/food
  ctx.fillStyle = highContrast ? '#ff0000' : '#FF4444';
  ctx.beginPath();
  ctx.arc(centerX, centerY, size / 2, 0, Math.PI * 2);
  ctx.fill();
  
  // Highlight
  ctx.fillStyle = '#ff8888';
  ctx.beginPath();
  ctx.arc(centerX - size * 0.2, centerY - size * 0.2, size * 0.3, 0, Math.PI * 2);
  ctx.fill();
};

const drawScanlines = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)';
  ctx.lineWidth = 1;
  for (let y = 0; y < h; y += 2) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
};

const drawHud = (
  ctx: CanvasRenderingContext2D,
  score: number,
  level: 1 | 2 | 3,
  canvasWidth: number
) => {
  ctx.save();
  ctx.font = 'bold 20px "Courier New", monospace';
  ctx.fillStyle = '#00FF00';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  
  // Score
  ctx.fillText(`SCORE: ${score.toString().padStart(4, '0')}`, 10, 10);
  
  // Level
  ctx.fillText(`LVL: ${level}`, canvasWidth - 80, 10);
  
  ctx.restore();
};

const drawPauseOverlay = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
  // Semi-transparent overlay
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(0, 0, w, h);
  
  // PAUSED text
  ctx.save();
  ctx.font = 'bold 48px "Courier New", monospace';
  ctx.fillStyle = '#00FF00';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('PAUSED', w / 2, h / 2);
  ctx.restore();
};
